import api from './api';
import { API_ENDPOINTS, USE_MOCK_DATA } from '../constants/config';
import { 
  mockEngineers, 
  generateMockEngineers, 
  mockTeamLeads,
  mockTickets,
  persistMockTickets,
  getEngineerTickets,
  getEngineerStats,
  getEngineerWeeklyPerformance,
  getEngineerPriorityBreakdown,
  getEngineerCurrentTickets
} from '../data/mockData';
import { getMockCompanies } from '../data/mockData';
import siteService from './siteService';
import { TICKET_STATUSES } from '../constants/statuses';
import { ROLES } from '../constants/roles';

const hasActiveLeave = (leaves = []) => {
  const today = new Date();
  return leaves.some((leave) => {
    if (leave.revoked_at) return false;
    const start = new Date(leave.start_date);
    const end = new Date(leave.end_date);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return false;
    return today >= start && today <= end;
  });
};

const applyLeaveStatus = (engineer) => {
  const leaves = Array.isArray(engineer.leaves) ? engineer.leaves : [];
  if (hasActiveLeave(leaves)) {
    return { ...engineer, status: 'On Leave', leaves };
  }

  if (engineer.status === 'On Leave') {
    return { ...engineer, status: 'Available', leaves };
  }

  return { ...engineer, leaves };
};

class EngineerService {
  async getAllEngineers(teamLeadId = null, siteId = null) {
    if (USE_MOCK_DATA) {
      return new Promise((resolve) => {
        setTimeout(() => {
          let filtered = mockEngineers.map(applyLeaveStatus).map(eng => {
            const engTickets = mockTickets.filter(t => Number(t.assignee_id) === Number(eng.id));
            const closedTkts = engTickets.filter(t => t.status === TICKET_STATUSES.CLOSED);
            const avgResponseTime = closedTkts.length > 0 
              ? Number((closedTkts.reduce((sum, t) => {
                  const duration = (new Date(t.closed_at) - new Date(t.created_at)) / (1000 * 60 * 60);
                  return sum + (Number.isNaN(duration) ? 24 : duration);
                }, 0) / closedTkts.length).toFixed(1))
              : Number(eng.avg_resolution_time || 0);
            return {
              ...eng,
              avg_response_time: avgResponseTime
            };
          });
          if (teamLeadId) {
            filtered = filtered.filter(e => e.team_lead_id === teamLeadId);
          }
          if (siteId !== 0 && siteId !== null && siteId !== undefined) {
            const numericSiteId = Number(siteId);
            filtered = filtered.map(eng => {
              const engTickets = mockTickets.filter(t => Number(t.assignee_id) === Number(eng.id) && Number(t.site_id) === numericSiteId);
              
              const activeTicketsCount = engTickets.filter(t => t.status !== TICKET_STATUSES.CLOSED).length;
              const completedTicketsCount = engTickets.filter(t => t.status === TICKET_STATUSES.CLOSED).length;
              
              // Recalculate performance score: SLA compliance on these site tickets
              const onTime = engTickets.filter(t => t.status === TICKET_STATUSES.CLOSED && !t.metrics?.is_overdue).length;
              const performanceScore = completedTicketsCount > 0 ? Math.round((onTime / completedTicketsCount) * 100) : 85;
              
              // Recalculate avg response time for this site
              const closedTkts = engTickets.filter(t => t.status === TICKET_STATUSES.CLOSED);
              const avgResponseTime = closedTkts.length > 0 
                ? Number((closedTkts.reduce((sum, t) => {
                    const duration = (new Date(t.closed_at) - new Date(t.created_at)) / (1000 * 60 * 60);
                    return sum + (Number.isNaN(duration) ? 24 : duration);
                  }, 0) / closedTkts.length).toFixed(1))
                : 0.0;
              
              return {
                ...eng,
                active_tickets: activeTicketsCount,
                completed_this_month: completedTicketsCount,
                performance_score: performanceScore,
                avg_response_time: avgResponseTime
              };
            });
          }
          resolve({ success: true, data: filtered });
        }, 300);
      });
    }

    try {
      let endpoint = API_ENDPOINTS.USERS_BY_ROLE(ROLES.ENGINEER);
      if (teamLeadId) {
        endpoint += `&team_lead_id=${teamLeadId}`;
      }
      if (siteId !== 0 && siteId !== null && siteId !== undefined) {
        endpoint += `&site_id=${siteId}`;
      }
      const response = await api.get(endpoint);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching engineers:', error);
      return { success: false, error: error.message };
    }
  }

  async getEngineerById(engineerId) {
    if (USE_MOCK_DATA) {
      return new Promise((resolve) => {
        setTimeout(() => {
          const engineer = mockEngineers.find(e => e.id === engineerId);
          if (engineer) {
            resolve({ success: true, data: applyLeaveStatus(engineer) });
          } else {
            resolve({ success: false, error: 'Engineer not found' });
          }
        }, 300);
      });
    }

    try {
      const response = await api.get(API_ENDPOINTS.USER_DETAILS(engineerId));
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching engineer:', error);
      return { success: false, error: error.message };
    }
  }

  async createEngineer(engineerData) {
    if (USE_MOCK_DATA) {
      return new Promise((resolve) => {
        setTimeout(async () => {
          const companyId = engineerData.company_id !== undefined && engineerData.company_id !== null && engineerData.company_id !== ''
            ? Number(engineerData.company_id)
            : null;
          const siteId = engineerData.site_id !== undefined && engineerData.site_id !== null && engineerData.site_id !== ''
            ? Number(engineerData.site_id)
            : null;
          const teamLeadId = engineerData.team_lead_id !== undefined && engineerData.team_lead_id !== null && engineerData.team_lead_id !== ''
            ? Number(engineerData.team_lead_id)
            : null;

          const selectedCompany = companyId !== null
            ? getMockCompanies().find((company) => company.id === companyId)
            : null;
          const sitesResult = await siteService.getAllSites();
          const selectedSite = sitesResult.success && siteId !== null
            ? sitesResult.data.find((site) => site.id === siteId)
            : null;
          const selectedTeamLead = teamLeadId !== null
            ? mockTeamLeads.find((tl) => tl.id === teamLeadId)
            : null;

          const newEngineer = {
            id: mockEngineers.length + 3,
            ...engineerData,
            company_id: companyId,
            site_id: siteId,
            team_lead_id: teamLeadId,
            team_lead: selectedTeamLead
              ? { id: selectedTeamLead.id, name: selectedTeamLead.name }
              : null,
            company_name: selectedCompany?.company_name || 'Unassigned',
            company: { company_name: selectedCompany?.company_name || 'Unassigned' },
            site_name: selectedSite?.name || selectedSite?.site_name || 'No Site',
            site: { name: selectedSite?.name || selectedSite?.site_name || 'No Site' },
            role: ROLES.ENGINEER,
            department: engineerData.department_id ? 
              { id: engineerData.department_id, name: 'Field Operations' } : null,
            status: 'Available',
            location: 'Office',
            active_tickets: 0,
            completed_today: 0,
            completed_this_month: 0,
            performance_score: 85,
            avg_resolution_time: 24,
            password_last_updated: new Date().toISOString(),
            is_locked_out: false,
            mfa_enabled: false
          };
          
          mockEngineers.unshift(newEngineer);
          resolve({ success: true, data: newEngineer });
        }, 500);
      });
    }

    try {
      const response = await api.post(API_ENDPOINTS.USERS, { ...engineerData, role: ROLES.ENGINEER });
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error creating engineer:', error);
      return { success: false, error: error.response?.data?.detail || error.message };
    }
  }

  async updateEngineer(engineerId, engineerData) {
    if (USE_MOCK_DATA) {
      return new Promise((resolve) => {
        setTimeout(() => {
          const engineerIndex = mockEngineers.findIndex(e => e.id === engineerId);
          if (engineerIndex !== -1) {
            mockEngineers[engineerIndex] = {
              ...mockEngineers[engineerIndex],
              ...engineerData,
              department: engineerData.department_id ? 
                { id: engineerData.department_id, name: 'Field Operations' } : mockEngineers[engineerIndex].department
            };
            resolve({ success: true, data: mockEngineers[engineerIndex] });
          } else {
            resolve({ success: false, error: 'Engineer not found' });
          }
        }, 500);
      });
    }

    try {
      const response = await api.put(API_ENDPOINTS.USER_DETAILS(engineerId), engineerData);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error updating engineer:', error);
      return { success: false, error: error.response?.data?.detail || error.message };
    }
  }

  async deleteEngineer(engineerId) {
    if (USE_MOCK_DATA) {
      return new Promise((resolve) => {
        setTimeout(() => {
          const engineerIndex = mockEngineers.findIndex(e => e.id === engineerId);
          if (engineerIndex !== -1) {
            const engineer = mockEngineers[engineerIndex];
            if (engineer.active_tickets > 0) {
              resolve({ 
                success: false, 
                error: `Cannot delete engineer with ${engineer.active_tickets} active tickets` 
              });
            } else {
              mockEngineers.splice(engineerIndex, 1);
              resolve({ success: true });
            }
          } else {
            resolve({ success: false, error: 'Engineer not found' });
          }
        }, 500);
      });
    }

    try {
      await api.delete(API_ENDPOINTS.USER_DETAILS(engineerId));
      return { success: true };
    } catch (error) {
      console.error('Error deleting engineer:', error);
      return { success: false, error: error.response?.data?.detail || error.message };
    }
  }

  async getAvailableEngineers() {
    if (USE_MOCK_DATA) {
      return new Promise((resolve) => {
        setTimeout(() => {
          const available = mockEngineers.filter(e => 
            e.is_active && ['Available', 'Active', 'Busy'].includes(e.status)
          );
          resolve({ success: true, data: available });
        }, 300);
      });
    }

    try {
      const response = await api.get(`${API_ENDPOINTS.USERS_BY_ROLE(ROLES.ENGINEER)}&status=Available`);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching available engineers:', error);
      return { success: false, error: error.message };
    }
  }

  async getEngineerPerformance(engineerId) {
    if (USE_MOCK_DATA) {
      return new Promise((resolve) => {
        setTimeout(() => {
          const engineer = mockEngineers.find(e => e.id === engineerId);
          if (engineer) {
            resolve({ 
              success: true, 
              data: {
                engineer_id: engineerId,
                performance_score: engineer.performance_score,
                completed_this_month: engineer.completed_this_month,
                active_tickets: engineer.active_tickets,
                avg_resolution_time: engineer.avg_resolution_time
              }
            });
          } else {
            resolve({ success: false, error: 'Engineer not found' });
          }
        }, 300);
      });
    }

    try {
      const response = await api.get(`${API_ENDPOINTS.USER_DETAILS(engineerId)}/performance`);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching engineer performance:', error);
      return { success: false, error: error.message };
    }
  }

  async getEngineerTL(engineerId) {
    if (USE_MOCK_DATA) {
      return new Promise((resolve) => {
        setTimeout(() => {
          const engineer = mockEngineers.find(e => e.id === engineerId);
          if (engineer && engineer.team_lead) {
            resolve({ success: true, data: engineer.team_lead });
          } else if (engineer) {
            resolve({ success: true, data: null });
          } else {
            resolve({ success: false, error: 'Engineer not found' });
          }
        }, 300);
      });
    }

    try {
      const response = await api.get(`${API_ENDPOINTS.USER_DETAILS(engineerId)}/team-lead`);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching engineer TL:', error);
      return { success: false, error: error.response?.data?.detail || error.message };
    }
  }

  async getAllTeamLeads() {
    if (USE_MOCK_DATA) {
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve({ success: true, data: mockTeamLeads });
        }, 300);
      });
    }

    try {
      const response = await api.get(API_ENDPOINTS.USERS_BY_ROLE(ROLES.TL));
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching team leads:', error);
      return { success: false, error: error.message };
    }
  }

  async updateEngineerTL(engineerId, teamLeadId) {
    if (USE_MOCK_DATA) {
      return new Promise((resolve) => {
        setTimeout(() => {
          const engineerIndex = mockEngineers.findIndex(e => e.id === engineerId);
          if (engineerIndex !== -1) {
            if (teamLeadId) {
              const tl = mockTeamLeads.find(t => t.id === teamLeadId);
              if (tl) {
                mockEngineers[engineerIndex].team_lead_id = teamLeadId;
                mockEngineers[engineerIndex].team_lead = {
                  id: tl.id,
                  name: tl.name,
                  email: tl.email
                };
              }
            } else {
              mockEngineers[engineerIndex].team_lead_id = null;
              mockEngineers[engineerIndex].team_lead = null;
            }
            resolve({ success: true, data: mockEngineers[engineerIndex] });
          } else {
            resolve({ success: false, error: 'Engineer not found' });
          }
        }, 500);
      });
    }

    try {
      const response = await api.put(API_ENDPOINTS.ENGINEER_UPDATE_TL(engineerId), {
        team_lead_id: teamLeadId
      });
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error updating engineer TL:', error);
      return { success: false, error: error.response?.data?.detail || error.message };
    }
  }

  async getDashboardData(engineerId) {
    if (USE_MOCK_DATA) {
      return new Promise((resolve) => {
        setTimeout(() => {
          const stats = getEngineerStats(engineerId);
          const weeklyPerformance = getEngineerWeeklyPerformance(engineerId);
          const priorityBreakdown = getEngineerPriorityBreakdown(engineerId);
          const currentTickets = getEngineerCurrentTickets(engineerId);
          
          resolve({
            success: true,
            data: { stats, weeklyPerformance, priorityBreakdown, currentTickets }
          });
        }, 300);
      });
    }

    try {
      const response = await api.get(API_ENDPOINTS.ENGINEER_DASHBOARD(engineerId));
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching engineer dashboard:', error);
      return { success: false, error: error.message };
    }
  }

  async getMyTickets(engineerId) {
    if (USE_MOCK_DATA) {
      return new Promise((resolve) => {
        setTimeout(() => {
          const tickets = getEngineerTickets(engineerId);
          resolve({ success: true, data: tickets });
        }, 300);
      });
    }

    try {
      const response = await api.get(`${API_ENDPOINTS.TICKETS}?assignee_id=${engineerId}`);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching engineer tickets:', error);
      return { success: false, error: error.message };
    }
  }

  async startWork(ticketId) {
    if (USE_MOCK_DATA) {
      return new Promise((resolve) => {
        setTimeout(() => {
          const ticketIndex = mockTickets.findIndex(t => t.id === ticketId);
          
          if (ticketIndex !== -1) {
            mockTickets[ticketIndex].status = TICKET_STATUSES.WORK_IN_PROGRESS;
            persistMockTickets();
            resolve({ success: true, data: mockTickets[ticketIndex] });
          } else {
            resolve({ success: false, error: 'Ticket not found' });
          }
        }, 500);
      });
    }

    try {
      const response = await api.post(API_ENDPOINTS.TICKET_START_WORK(ticketId));
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error starting work:', error);
      return { success: false, error: error.response?.data?.detail || error.message };
    }
  }

  async getTodaySchedule(engineerId) {
    if (USE_MOCK_DATA) {
      return new Promise((resolve) => {
        setTimeout(() => {
          const { getEngineerTodaySchedule } = require('../data/mockData');
          const schedule = getEngineerTodaySchedule(engineerId);
          resolve({ success: true, data: schedule });
        }, 300);
      });
    }

    try {
      const response = await api.get(`${API_ENDPOINTS.ENGINEER_SCHEDULE(engineerId)}/today`);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching schedule:', error);
      return { success: false, error: error.message };
    }
  }

  async getScheduleStats(engineerId) {
    if (USE_MOCK_DATA) {
      return new Promise((resolve) => {
        setTimeout(() => {
          const { getEngineerScheduleStats } = require('../data/mockData');
          const stats = getEngineerScheduleStats(engineerId);
          resolve({ success: true, data: stats });
        }, 300);
      });
    }

    try {
      const response = await api.get(`${API_ENDPOINTS.ENGINEER_SCHEDULE(engineerId)}/stats`);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching schedule stats:', error);
      return { success: false, error: error.message };
    }
  }

  async completeScheduleItem(scheduleId) {
    if (USE_MOCK_DATA) {
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve({ success: true });
        }, 500);
      });
    }

    try {
      const response = await api.put(`/api/v1/schedule/${scheduleId}/complete`);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error completing schedule item:', error);
      return { success: false, error: error.response?.data?.detail || error.message };
    }
  }

  async getWorkReports(engineerId) {
    if (USE_MOCK_DATA) {
      return new Promise((resolve) => {
        setTimeout(() => {
          const { getEngineerWorkReports } = require('../data/mockData');
          const reports = getEngineerWorkReports(engineerId);
          resolve({ success: true, data: reports });
        }, 300);
      });
    }

    try {
      const response = await api.get(`${API_ENDPOINTS.WORK_REPORTS}?engineer_id=${engineerId}`);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching work reports:', error);
      return { success: false, error: error.message };
    }
  }

  async getReportableTickets(engineerId) {
    if (USE_MOCK_DATA) {
      return new Promise((resolve) => {
        setTimeout(() => {
          const { getWorkReportableTickets } = require('../data/mockData');
          const tickets = getWorkReportableTickets(engineerId);
          resolve({ success: true, data: tickets });
        }, 300);
      });
    }

    try {
      const response = await api.get(`${API_ENDPOINTS.TICKETS}?assignee_id=${engineerId}&status=${TICKET_STATUSES.WORK_IN_PROGRESS}`);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching reportable tickets:', error);
      return { success: false, error: error.message };
    }
  }

  async createWorkReport(reportData) {
    try {
      const response = await api.post(API_ENDPOINTS.WORK_REPORTS, reportData);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error creating work report:', error);
      return { success: false, error: error.response?.data?.detail || error.message };
    }
  }

  async updateWorkReport(reportId, reportData) {
    try {
      const response = await api.put(API_ENDPOINTS.WORK_REPORT_DETAILS(reportId), reportData);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error updating work report:', error);
      return { success: false, error: error.response?.data?.detail || error.message };
    }
  }

  async submitWorkReport(reportId) {
    try {
      const response = await api.post(`/api/v1/work-reports/${reportId}/submit`);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error submitting work report:', error);
      return { success: false, error: error.response?.data?.detail || error.message };
    }
  }

  async getChecklistTypes() {
    if (USE_MOCK_DATA) {
      return new Promise((resolve) => {
        setTimeout(() => {
          const { getChecklistTypes } = require('../data/mockData');
          const types = getChecklistTypes();
          resolve({ success: true, data: types });
        }, 300);
      });
    }

    try {
      const response = await api.get('/api/v1/engineer/checklists'); // Keeping literal if missing in config
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching checklist types:', error);
      return { success: false, error: error.message };
    }
  }

  async getChecklistItems(checklistType) {
    if (USE_MOCK_DATA) {
      return new Promise((resolve) => {
        setTimeout(() => {
          const { getChecklistItems } = require('../data/mockData');
          const items = getChecklistItems(checklistType);
          resolve({ success: true, data: items });
        }, 300);
      });
    }

    try {
      const response = await api.get(`/api/v1/engineer/checklists/${checklistType}`);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching checklist items:', error);
      return { success: false, error: error.message };
    }
  }

  async submitChecklist(checklistData) {
    if (USE_MOCK_DATA) {
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve({ success: true, data: { id: Math.random(), ...checklistData } });
        }, 500);
      });
    }

    try {
      const response = await api.post('/api/v1/engineer/checklists/submit', checklistData);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error submitting checklist:', error);
      return { success: false, error: error.response?.data?.detail || error.message };
    }
  }

  async getKnowledgeBaseCategories() {
    if (USE_MOCK_DATA) {
      return new Promise((resolve) => {
        setTimeout(() => {
          const { getKnowledgeBaseCategories } = require('../data/mockData');
          const categories = getKnowledgeBaseCategories();
          resolve({ success: true, data: categories });
        }, 300);
      });
    }

    try {
      const response = await api.get(API_ENDPOINTS.KNOWLEDGE_BASE_CATEGORIES);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching knowledge base categories:', error);
      return { success: false, error: error.message };
    }
  }

  async getKnowledgeBaseArticles(searchTerm = '') {
    if (USE_MOCK_DATA) {
      return new Promise((resolve) => {
        setTimeout(() => {
          const { getKnowledgeBaseArticles } = require('../data/mockData');
          const articles = getKnowledgeBaseArticles(searchTerm);
          resolve({ success: true, data: articles });
        }, 300);
      });
    }

    try {
      const endpoint = searchTerm 
        ? `${API_ENDPOINTS.KNOWLEDGE_BASE_SEARCH}?q=${encodeURIComponent(searchTerm)}`
        : API_ENDPOINTS.KNOWLEDGE_BASE_ARTICLES;
      const response = await api.get(endpoint);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching knowledge base articles:', error);
      return { success: false, error: error.message };
    }
  }

  async getKnowledgeBaseArticle(articleId) {
    if (USE_MOCK_DATA) {
      return new Promise((resolve) => {
        setTimeout(() => {
          const { getKnowledgeBaseArticle } = require('../data/mockData');
          const article = getKnowledgeBaseArticle(articleId);
          if (article) {
            resolve({ success: true, data: article });
          } else {
            resolve({ success: false, error: 'Article not found' });
          }
        }, 300);
      });
    }

    try {
      const response = await api.get(API_ENDPOINTS.KNOWLEDGE_BASE_ARTICLE(articleId));
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching knowledge base article:', error);
      return { success: false, error: error.message };
    }
  }

  async getEmergencyContacts() {
    if (USE_MOCK_DATA) {
      return new Promise((resolve) => {
        setTimeout(() => {
          const { getEmergencyContacts } = require('../data/mockData');
          const contacts = getEmergencyContacts();
          resolve({ success: true, data: contacts });
        }, 300);
      });
    }

    try {
      const response = await api.get(API_ENDPOINTS.EMERGENCY_CONTACTS);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching emergency contacts:', error);
      return { success: false, error: error.message };
    }
  }

  async uploadTicketAttachment(ticketId, file) {
    // If mock data, just pretend success
    if (USE_MOCK_DATA) {
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve({ success: true, data: { file_name: file.name, s3_object_key: `mock/path/${file.name}` } });
        }, 300);
      });
    }

    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post(`/api/v1/tickets/${ticketId}/attachments`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return { success: true, data: response.data };
    } catch (error) {
      console.error(`Error uploading attachment for ticket ${ticketId}:`, error);
      return { success: false, error: error.response?.data?.detail || error.message };
    }
  }
  async uploadWorkReportAttachment(file) {
    if (USE_MOCK_DATA) {
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve({ success: true, data: { file_name: file.name, s3_object_key: `mock/work_reports/${file.name}` } });
        }, 300);
      });
    }

    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post('/api/v1/work-reports/upload-attachment', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return { success: true, data: response.data };
    } catch (error) {
      console.error(`Error uploading work report attachment:`, error);
      return { success: false, error: error.response?.data?.detail || error.message };
    }
  }

  async getEngineerLeaves(engineerId) {
    if (USE_MOCK_DATA) {
      return new Promise((resolve) => {
        setTimeout(() => {
          const engineer = mockEngineers.find((e) => e.id === engineerId);
          if (!engineer) {
            resolve({ success: false, error: 'Engineer not found' });
            return;
          }

          const leaves = Array.isArray(engineer.leaves) ? engineer.leaves : [];
          resolve({ success: true, data: leaves });
        }, 250);
      });
    }

    try {
      const response = await api.get(`/api/v1/users/${engineerId}/leaves`);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching engineer leaves:', error);
      return { success: false, error: error.response?.data?.detail || error.message };
    }
  }

  async markEngineerOnLeave(engineerId, leaveData) {
    if (USE_MOCK_DATA) {
      return new Promise((resolve) => {
        setTimeout(() => {
          const engineerIndex = mockEngineers.findIndex((e) => e.id === engineerId);
          if (engineerIndex === -1) {
            resolve({ success: false, error: 'Engineer not found' });
            return;
          }

          const currentLeaves = Array.isArray(mockEngineers[engineerIndex].leaves)
            ? mockEngineers[engineerIndex].leaves
            : [];

          const newLeave = {
            id: Date.now(),
            start_date: leaveData.start_date,
            end_date: leaveData.end_date,
            reason: leaveData.reason || '',
            created_at: new Date().toISOString(),
            revoked_at: null
          };

          const updatedEngineer = applyLeaveStatus({
            ...mockEngineers[engineerIndex],
            leaves: [newLeave, ...currentLeaves]
          });
          mockEngineers[engineerIndex] = updatedEngineer;

          resolve({ success: true, data: newLeave });
        }, 350);
      });
    }

    try {
      const response = await api.post(`/api/v1/users/${engineerId}/leaves`, leaveData);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error marking engineer on leave:', error);
      return { success: false, error: error.response?.data?.detail || error.message };
    }
  }

  async revokeEngineerLeave(engineerId, leaveId) {
    if (USE_MOCK_DATA) {
      return new Promise((resolve) => {
        setTimeout(() => {
          const engineerIndex = mockEngineers.findIndex((e) => e.id === engineerId);
          if (engineerIndex === -1) {
            resolve({ success: false, error: 'Engineer not found' });
            return;
          }

          const currentLeaves = Array.isArray(mockEngineers[engineerIndex].leaves)
            ? mockEngineers[engineerIndex].leaves
            : [];

          const leaveExists = currentLeaves.some((leave) => leave.id === leaveId);
          if (!leaveExists) {
            resolve({ success: false, error: 'Leave entry not found' });
            return;
          }

          const updatedLeaves = currentLeaves.map((leave) => (
            leave.id === leaveId
              ? { ...leave, revoked_at: new Date().toISOString() }
              : leave
          ));

          const updatedEngineer = applyLeaveStatus({
            ...mockEngineers[engineerIndex],
            leaves: updatedLeaves
          });
          mockEngineers[engineerIndex] = updatedEngineer;

          resolve({ success: true, data: updatedLeaves.find((leave) => leave.id === leaveId) });
        }, 350);
      });
    }

    try {
      const response = await api.delete(`/api/v1/users/${engineerId}/leaves/${leaveId}`);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error revoking engineer leave:', error);
      return { success: false, error: error.response?.data?.detail || error.message };
    }
  }
}

export default new EngineerService();